'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DayOfTheWeekCalculator() {
  const t = useTranslations("tools.dayOfTheWeekCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    month: '',
    day: '',
    year: ''
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    // Set today's date on mount
    const today = new Date();
    setInputs({
      month: (today.getMonth() + 1).toString(),
      day: today.getDate().toString(),
      year: today.getFullYear().toString()
    });
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const isLeapYear = (year: number): boolean => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  };

  const getDayOfYear = (date: Date): number => {
    const start = new Date(date.getFullYear(), 0, 1);
    const diff = date.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay) + 1;
  };

  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  const getDaysRemainingInYear = (date: Date): number => {
    const endOfYear = new Date(date.getFullYear(), 11, 31);
    const diff = endOfYear.getTime() - date.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };

  const generateCalendar = (year: number, month: number, selectedDay: number) => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const calendar = [];
    const today = new Date();
    const isCurrentMonth = today.getMonth() === month - 1 && today.getFullYear() === year;

    for (let i = 0; i < startingDayOfWeek; i++) {
      calendar.push({ day: 0, type: 'empty' });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      let type = 'normal';
      if (day === selectedDay) {
        type = 'selected';
      } else if (isCurrentMonth && day === today.getDate()) {
        type = 'today';
      }
      calendar.push({ day, type });
    }

    return calendar;
  };

  const getFunFacts = (dayOfWeek: string): string[] => {
    const funFactsData: { [key: string]: string[] } = {
      'Sunday': [
        t('funFactSunday1'),
        t('funFactSunday2'),
        t('funFactSunday3'),
        t('funFactSunday4')
      ],
      'Monday': [
        t('funFactMonday1'),
        t('funFactMonday2'),
        t('funFactMonday3'),
        t('funFactMonday4')
      ],
      'Tuesday': [
        t('funFactTuesday1'),
        t('funFactTuesday2'),
        t('funFactTuesday3'),
        t('funFactTuesday4')
      ],
      'Wednesday': [
        t('funFactWednesday1'),
        t('funFactWednesday2'),
        t('funFactWednesday3'),
        t('funFactWednesday4')
      ],
      'Thursday': [
        t('funFactThursday1'),
        t('funFactThursday2'),
        t('funFactThursday3'),
        t('funFactThursday4')
      ],
      'Friday': [
        t('funFactFriday1'),
        t('funFactFriday2'),
        t('funFactFriday3'),
        t('funFactFriday4')
      ],
      'Saturday': [
        t('funFactSaturday1'),
        t('funFactSaturday2'),
        t('funFactSaturday3'),
        t('funFactSaturday4')
      ]
    };
    return funFactsData[dayOfWeek] || [];
  };

  const handleQuickDate = (type: 'today' | 'tomorrow' | 'yesterday') => {
    const date = new Date();
    if (type === 'tomorrow') {
      date.setDate(date.getDate() + 1);
    } else if (type === 'yesterday') {
      date.setDate(date.getDate() - 1);
    }
    setInputs({
      month: (date.getMonth() + 1).toString(),
      day: date.getDate().toString(),
      year: date.getFullYear().toString()
    });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const month = parseInt(inputs.month);
    const day = parseInt(inputs.day);
    const year = parseInt(inputs.year);

    if (!month || !day || !year) {
      setError(t('errorInvalidDate'));
      return;
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      setError(t('errorInvalidDay'));
      return;
    }

    const date = new Date(year, month - 1, day);
    const dayOfWeek = dayNames[date.getDay()];
    const calendar = generateCalendar(year, month, day);
    const funFacts = getFunFacts(dayOfWeek);

    setResults({
      dayOfWeek,
      fullDate: `${monthNames[month - 1]} ${day}, ${year}`,
      dayOfYear: getDayOfYear(date),
      weekNumber: getWeekNumber(date),
      daysRemaining: getDaysRemainingInYear(date),
      isLeapYear: isLeapYear(year),
      calendar,
      calendarHeader: `${monthNames[month - 1]} ${year}`,
      funFacts
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    {},
    { maxWidth: '700px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const dateInputGroupStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr 1fr' }
  );

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const
  };

  const quickDatesStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' as const, marginTop: '1rem' },
    {},
    {},
    {}
  );

  const quickDateBtnStyle = getResponsiveStyle(
    {
      padding: '0.5rem 1rem',
      background: '#f1f5f9',
      border: '1px solid #cbd5e1',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '0.875rem'
    },
    {},
    {},
    {}
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '1rem',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      marginTop: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
    { fontSize: '1.3rem' },
    { fontSize: '1.35rem' },
    { fontSize: '1.4rem' }
  );

  const dayResultStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      textAlign: 'center' as const,
      marginBottom: '2rem',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const dayNameStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '0.5rem'
    },
    { fontSize: '2.5rem' },
    { fontSize: '2.75rem' },
    { fontSize: '3rem' }
  );

  const fullDateStyle = getResponsiveStyle(
    { color: '#64748b', marginBottom: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
      borderLeft: '4px solid #2563eb'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultCardLabelStyle = {
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500
  };

  const resultCardValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' }
  );

  const calendarDisplayStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const calendarHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const calendarGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '0.5rem'
  };

  const calendarDayHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, fontWeight: 600, color: '#64748b', padding: '0.5rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.8375rem' },
    { fontSize: '0.875rem' }
  );

  const calendarDayStyle = getResponsiveStyle(
    { textAlign: 'center' as const, padding: '0.75rem', borderRadius: '0.5rem', color: '#475569' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const funFactsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      borderLeft: '4px solid #f59e0b'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const funFactsH4Style = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: '#92400e' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>📅 {t("toolTitle")}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>{t("selectDateLabel")}</label>
              <div style={dateInputGroupStyle}>
                <select
                  style={inputStyle}
                  value={inputs.month}
                  onChange={(e) => setInputs({...inputs, month: e.target.value})}
                  required
                >
                  <option value="">{t("monthPlaceholder")}</option>
                  <option value="1">{t("january")}</option>
                  <option value="2">{t("february")}</option>
                  <option value="3">{t("march")}</option>
                  <option value="4">{t("april")}</option>
                  <option value="5">{t("may")}</option>
                  <option value="6">{t("june")}</option>
                  <option value="7">{t("july")}</option>
                  <option value="8">{t("august")}</option>
                  <option value="9">{t("september")}</option>
                  <option value="10">{t("october")}</option>
                  <option value="11">{t("november")}</option>
                  <option value="12">{t("december")}</option>
                </select>
                <input
                  type="number"
                  style={inputStyle}
                  placeholder={t("dayPlaceholder")}
                  value={inputs.day}
                  onChange={(e) => setInputs({...inputs, day: e.target.value})}
                  min="1"
                  max="31"
                  required
                />
                <input
                  type="number"
                  style={inputStyle}
                  placeholder={t("yearPlaceholder")}
                  value={inputs.year}
                  onChange={(e) => setInputs({...inputs, year: e.target.value})}
                  min="1"
                  max="9999"
                  required
                />
              </div>
              <div style={quickDatesStyle}>
                <button
                  type="button"
                  style={quickDateBtnStyle}
                  onClick={() => handleQuickDate('today')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.borderColor = '#94a3b8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                >
                  {t("todayButton")}
                </button>
                <button
                  type="button"
                  style={quickDateBtnStyle}
                  onClick={() => handleQuickDate('tomorrow')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.borderColor = '#94a3b8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                >
                  {t("tomorrowButton")}
                </button>
                <button
                  type="button"
                  style={quickDateBtnStyle}
                  onClick={() => handleQuickDate('yesterday')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.borderColor = '#94a3b8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                >
                  {t("yesterdayButton")}
                </button>
              </div>
            </div>

            <button
              type="submit"
              style={calculateBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t("calculateButton")}
            </button>

            {error && (
              <div style={errorMessageStyle}>{error}</div>
            )}
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t("resultsTitle")}</h3>

              <div style={dayResultStyle}>
                <div style={dayNameStyle}>{results.dayOfWeek}</div>
                <div style={fullDateStyle}>{results.fullDate}</div>
              </div>

              <div style={resultGridStyle}>
                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t("dayOfYearLabel")}</div>
                  <div style={resultCardValueStyle}>{results.dayOfYear}</div>
                </div>
                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t("weekNumberLabel")}</div>
                  <div style={resultCardValueStyle}>{results.weekNumber}</div>
                </div>
                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t("daysRemainingLabel")}</div>
                  <div style={resultCardValueStyle}>{results.daysRemaining}</div>
                </div>
                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t("leapYearLabel")}</div>
                  <div style={resultCardValueStyle}>{results.isLeapYear ? t("yes") : t("no")}</div>
                </div>
              </div>

              <div style={calendarDisplayStyle}>
                <div style={calendarHeaderStyle}>{results.calendarHeader}</div>
                <div style={calendarGridStyle}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <div key={index} style={calendarDayHeaderStyle}>{day}</div>
                  ))}
                  {results.calendar.map((item: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        ...calendarDayStyle,
                        visibility: item.type === 'empty' ? 'hidden' as const : 'visible' as const,
                        background: item.type === 'selected' ? '#f59e0b' : item.type === 'today' ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'transparent',
                        color: item.type === 'selected' || item.type === 'today' ? 'white' : '#475569',
                        fontWeight: item.type === 'selected' || item.type === 'today' ? 700 : 'normal'
                      }}
                    >
                      {item.day || ''}
                    </div>
                  ))}
                </div>
              </div>

              <div style={funFactsStyle}>
                <h4 style={funFactsH4Style}>🎯 {t("funFactsTitle")} {results.dayOfWeek}</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#78350f' }}>
                  {results.funFacts.map((fact: string, index: number) => (
                    <li key={index} style={{ marginBottom: '0.5rem', lineHeight: 1.6 }}>{fact}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li>{t("howToUseStep1")}</li>
          <li>{t("howToUseStep2")}</li>
          <li>{t("howToUseStep3")}</li>
          <li>{t("howToUseStep4")}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
      </section>

      {/* Calculation Methods */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("methodsTitle")}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t("methodZellerTitle")}</h3>
        <p style={paragraphStyle}>{t("methodZellerDescription")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("methodDoomsdayTitle")}</h3>
        <p style={paragraphStyle}>{t("methodDoomsdayDescription")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("methodRataDieTitle")}</h3>
        <p style={paragraphStyle}>{t("methodRataDieDescription")}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>
        <ul style={listStyle}>
          <li>{t("bestPractice1")}</li>
          <li>{t("bestPractice2")}</li>
          <li>{t("bestPractice3")}</li>
          <li>{t("bestPractice4")}</li>
          <li>{t("bestPractice5")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t("faq1Question")}</h3>
        <p style={paragraphStyle}>{t("faq1Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("faq2Question")}</h3>
        <p style={paragraphStyle}>{t("faq2Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("faq3Question")}</h3>
        <p style={paragraphStyle}>{t("faq3Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("faq4Question")}</h3>
        <p style={paragraphStyle}>{t("faq4Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t("faq5Question")}</h3>
        <p style={paragraphStyle}>{t("faq5Answer")}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li>
            <a href="https://en.wikipedia.org/wiki/Determination_of_the_day_of_the_week" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("reference1")}
            </a>
          </li>
          <li>
            <a href="https://www.timeanddate.com/date/doomsday-weekday.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("reference2")}
            </a>
          </li>
          <li>
            <a href="https://worldmentalcalculation.com/how-to-calculate-calendar-dates/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("reference3")}
            </a>
          </li>
          <li>
            <a href="https://www.calculator.net/day-of-the-week-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("reference4")}
            </a>
          </li>
          <li>
            <a href="https://aulis.org/Calendar/Day_of_the_Week.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}